import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';

const Nav = styled.nav`
  background: linear-gradient(135deg, #C44900 0%, #B73E3E 100%);
  padding: 1rem 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  position: sticky;
  top: 0;
  z-index: 10000;
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  font-size: 1.8rem;
  font-weight: bold;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    opacity: 0.9;
  }
`;

const LogoImage = styled.img`
  height: 50px;
  width: 50px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.3);

  @media (max-width: 768px) {
    height: 40px;
    width: 40px;
  }
`;

const NavLinks = styled.div<{ isOpen: boolean }>`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 250px;
    background: linear-gradient(135deg, #C44900 0%, #B73E3E 100%);
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 6rem 2rem 2rem;
    gap: 1.5rem;
    transform: translateX(${props => props.isOpen ? '0' : '100%'});
    transition: transform 0.3s ease-in-out;
    z-index: 10000;
    box-shadow: -2px 0 10px rgba(0,0,0,0.1);
  }
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 10001;
  position: relative;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  @media (max-width: 768px) {
    display: block;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9998;
  }
`;

const CloseButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    z-index: 10001;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255,255,255,0.2);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem;
    border-radius: 8px;
  }
`;

const AuthButton = styled.button`
  color: white;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.3);
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255,255,255,0.2);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem;
    border-radius: 8px;
  }
`;

const UserInfo = styled.span`
  color: white;
  font-weight: 500;
  margin-right: 1rem;

  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 0.5rem;
    width: 100%;
    padding: 0.5rem 1rem;
  }
`;

const AdminBadge = styled.span`
  background: rgba(255, 215, 0, 0.2);
  color: #ffd700;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
`;

const Navigation: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { isAdmin, checkPermissions } = useAdmin();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      setIsMobileMenuOpen(false);
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <Nav>
        <NavContainer>
          <Logo to="/" onClick={closeMobileMenu}>
            <LogoImage src="/images/playgrounds/Playground logo.png" alt="Playground Directory" />
            Playground Directory
          </Logo>
          <HamburgerButton onClick={toggleMobileMenu}>
            ☰
          </HamburgerButton>
          <NavLinks isOpen={isMobileMenuOpen}>
            <CloseButton onClick={closeMobileMenu}>×</CloseButton>
            <NavLink to="/" onClick={closeMobileMenu}>Home</NavLink>
            <NavLink to="/states" onClick={closeMobileMenu}>Browse States</NavLink>
            <NavLink to="/about" onClick={closeMobileMenu}>About Us</NavLink>
            <NavLink to="/contact" onClick={closeMobileMenu}>Contact Us</NavLink>
            {currentUser ? (
              <>
                {isAdmin && (
                  <>
                    <NavLink to="/admin" onClick={closeMobileMenu}>Admin Dashboard</NavLink>
                    <NavLink to="/admin/listings" onClick={closeMobileMenu}>Manage Listings</NavLink>
                    <NavLink to="/admin/inbox" onClick={closeMobileMenu}>Inbox</NavLink>
                  </>
                )}
                {!isAdmin && checkPermissions() && (
                  <NavLink to="/admin/listings" onClick={closeMobileMenu}>Manage Listings</NavLink>
                )}
                <UserInfo>
                  Hello, {currentUser.displayName || currentUser.email}
                  {isAdmin && <AdminBadge>ADMIN</AdminBadge>}
                </UserInfo>
                <AuthButton onClick={handleLogout}>Logout</AuthButton>
              </>
            ) : (
              <>
                <NavLink to="/login" onClick={closeMobileMenu}>Login</NavLink>
                <NavLink to="/signup" onClick={closeMobileMenu}>Sign Up</NavLink>
              </>
            )}
          </NavLinks>
        </NavContainer>
      </Nav>
      <Overlay isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />
    </>
  );
};

export default Navigation;